import { Link } from "react-router-dom";
import { ArrowUpRight, Clock } from "lucide-react";
import { POSTS } from "@/blog/posts";

/**
 * "From the lab" strip on the Landing page.
 * - Surfaces 3 newest blog posts (top-of-funnel content)
 * - Internal links from Landing → posts spread PageRank and boost ranking for
 *   the long-tail keywords those posts target
 * - Lightweight, scan-friendly, no images required
 */
export default function BlogStrip() {
  // Sort by publishedAt desc so newest 3 are always shown (resilient to posts.js re-ordering)
  const featured = [...POSTS]
    .sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt))
    .slice(0, 3);
  if (featured.length === 0) return null;

  return (
    <section className="bg-white border-b border-zinc-200" data-testid="blog-strip">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-14">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <div className="overline mb-3">◇ FROM THE LAB</div>
            <h2 className="font-display font-extrabold text-3xl lg:text-5xl tracking-tight leading-[1.05] max-w-xl">
              Field notes on<br /><span className="brand-red">what actually works.</span>
            </h2>
          </div>
          <Link
            to="/blog"
            data-testid="blog-strip-all-link"
            className="self-start md:self-auto inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-zinc-700 hover:text-[#EF4444] transition-colors"
          >
            View all field notes <ArrowUpRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {featured.map((post) => {
            const pretty = new Date(post.publishedAt).toLocaleDateString("en-US", {
              month: "short", day: "numeric", year: "numeric",
            });
            return (
              <Link
                key={post.slug}
                to={`/blog/${post.slug}`}
                data-testid={`blog-strip-card-${post.slug}`}
                className="group block bg-zinc-50 border border-zinc-200 hover:border-zinc-900 transition-colors h-full"
              >
                <div className={`bg-gradient-to-br ${post.heroAccent} p-6`}>
                  <div className="font-mono text-[9px] uppercase tracking-widest text-white/70 mb-3">◷ {post.tags[0]}</div>
                  <h3 className="font-display font-bold text-white text-lg leading-snug tracking-tight">
                    {post.title}
                  </h3>
                </div>
                <div className="p-6 flex flex-col h-[152px]">
                  <p className="text-zinc-600 text-sm leading-relaxed line-clamp-3 flex-1">{post.description}</p>
                  <div className="mt-4 pt-4 border-t border-zinc-200 flex items-center justify-between text-[11px] text-zinc-400 font-mono uppercase tracking-wider">
                    <span className="inline-flex items-center gap-1.5"><Clock className="w-3 h-3" />{post.readingMinutes} MIN</span>
                    <span className="text-zinc-500">{pretty}</span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
